import { ChangeDetectionStrategy, Component } from '@angular/core';
import { lockClick } from 'directives/cs-capture-cover/cs-capture-cover.directive';
import { CsPage, NavController } from 'providers/nav-controller';
import { TranslatedString } from 'models/translated-string';
import { ModalPageMarker } from 'providers/modal-page-marker';
import { NotePageParams, NotePageResult } from './note-page-interface';
import { confirmDiscardAsync } from 'providers/unsaved-changes-guard/unsaved-changes-guard';
import { AlertController } from 'providers/alert-controller';

import { LL } from 'i18n/i18n.service';
@Component({
  selector: 'page-note',
  templateUrl: 'note-page.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [ModalPageMarker]
})
export class NotePage extends CsPage<NotePageParams> {
  protected LL = LL;
  protected noteText: string = '';

  get isEditMode(): boolean {
    return !!this.navParams.data?.currentNote?.trim();
  }

  get title(): TranslatedString {
    const prefix = this.navParams.data?.translationPrefix || 'pinnedNote';
    if (prefix === 'setNote') {
      return this.isEditMode ? LL.setNote.titleEdit() : LL.setNote.title();
    }
    return this.isEditMode ? LL.pinnedNote.titleEdit() : LL.pinnedNote.title();
  }

  get description(): TranslatedString {
    const prefix = this.navParams.data?.translationPrefix || 'pinnedNote';
    if (prefix === 'setNote') {
      return LL.setNote.description();
    }
    return LL.pinnedNote.description();
  }

  get placeholder(): TranslatedString {
    const prefix = this.navParams.data?.translationPrefix || 'pinnedNote';
    if (prefix === 'setNote') {
      return LL.setNote.placeholder();
    }
    return LL.pinnedNote.placeholder();
  }

  constructor(
    public navCtrl: NavController,
    private alertCtrl: AlertController,
  ) {
    super();
  }

  ngOnInit() {
    this.noteText = this.navParams.data?.currentNote || '';
  }

  onNoteChange(value: string) {
    this.noteText = value;
  }

  async onSaveClick() {
    const result: NotePageResult = { success: true, note: this.noteText.trim() };
    this.leaveWithoutAsking.set();
    await this.navCtrl.popAsync(result);
  }

  onCancelClick(event: Event) {
    this.leaveWithoutAsking.set();
    lockClick(event, this.navCtrl.popAsync({ success: false }));
  }

  /**
   * Runs before the page closes, whichever way the user leaves: the browser back
   * button, Android's hardware back button or a swipe back. Without it, a note
   * the user had typed but not saved simply vanished (#5058).
   *
   * There is nothing to save the note INTO from here — saving means handing it
   * back to the page that asked for it, which is what the Save button does — so
   * the question is only whether the user meant to throw the typing away.
   */
  async ionViewCanLeave(): Promise<boolean> {
    // Save and Cancel arm this, having already settled what happens to the note
    if (this.leaveWithoutAsking.consume()) return true;
    if (!this.hasUnsavedNote()) return true;
    return confirmDiscardAsync(this.alertCtrl, {
      title: LL.unsavedChanges.discard.noteTitle(),
      message: LL.unsavedChanges.discard.noteMessage(),
    });
  }

  /** Whether the box holds something other than the note the page opened with. */
  private hasUnsavedNote(): boolean {
    return this.noteText.trim() !== (this.navParams.data?.currentNote || '').trim();
  }
}
